import { useLayoutEffect, useRef, useState } from 'react'

import type { DramaDetail, Episode } from '../../../api/drama'
import { t } from '../../../i18n'

/** 视频区下面那条黑带的高。视频区底边 = 屏幕底 − 56（红果 dzj.xml bottom_guide_line）。 */
export const BAND_HEIGHT = 56

/** 已上线集数到了运营填的总集数就算完结。 */
function finished(drama: DramaDetail) {
  return drama.totalEpisodes > 0 && drama.episodes.length >= drama.totalEpisodes
}

/**
 * 视频区下面 56 黑带里的选集条，照红果 cwv.xml：高 40、圆角 8、10% 白底，
 * 「选集 · 已完结 · 全 N 集」14 粗白字，右边一个上拉箭头；点开选集面板。
 * 和 App 的 EpisodeBand 同一个样子。
 */
export function EpisodeBand({ drama, onOpen }: { drama: DramaDetail; onOpen: () => void }) {
  const label = finished(drama)
    ? t.episodeBandDone.replace('{n}', String(drama.totalEpisodes))
    : t.episodeBandOngoing.replace('{n}', String(drama.episodes.length))
  return (
    <div style={band.wrap}>
      <button style={band.bar} onClick={onOpen}>
        <span style={band.label}>{label}</span>
        <svg width="13" height="8" viewBox="0 0 13 8" aria-hidden>
          <path d="M1.5 6.5 6.5 1.5l5 5" fill="none" stroke="#fff" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" />
        </svg>
      </button>
    </div>
  )
}

const band = {
  wrap: {
    flex: '0 0 auto',
    height: `calc(${BAND_HEIGHT}px + env(safe-area-inset-bottom))`,
    padding: '8px 16px env(safe-area-inset-bottom)',
    background: '#000',
  },
  bar: {
    width: '100%',
    height: 40,
    padding: '0 16px',
    display: 'flex',
    alignItems: 'center',
    gap: 12,
    borderRadius: 8,
    background: 'rgba(255,255,255,.1)',
    color: '#fff',
  },
  label: {
    flex: 1,
    minWidth: 0,
    textAlign: 'left',
    fontSize: 14,
    fontWeight: 700,
    overflow: 'hidden',
    textOverflow: 'ellipsis',
    whiteSpace: 'nowrap',
  },
} satisfies Record<string, React.CSSProperties>

/** 面板占屏高的比例。页面据此把视频缩进面板上方。 */
export const SHEET_RATIO = 0.6
const PAGE_SIZE = 30

interface Props {
  open: boolean
  drama: DramaDetail
  currentId: number | null
  /** 当前这一集正在播（不是暂停）。音柱跟着它动或停。 */
  playing: boolean
  onSelect: (episode: Episode) => void
}

/**
 * 选集面板，照红果 ShortSeriesEpisodesDialogV2，和 App 的 EpisodeSheet 同一套（docs/App界面规格-红果取证.md 第 12 节）。
 *
 * 占屏高 60%，白底、上两角 16 圆角，**不压暗后面**：视频缩在上方照常播，点上方空白处收起（页面那层透明遮罩管）。
 * 顶部 36×4 拖动条；剧头 36×50 封面、剧名 16 粗、「全 N 集」14 三级字；简介 14、最多 2 行、截断时末尾「展开」；
 * 标签 24 高、4 圆角；超过 30 集分段；每段 6 列格子：当前集主色粗体、主色 10% 底、右上角音柱；
 * 看过的 40% 黑；锁住的 20% 黑带锁；其余黑字。
 *
 * `locked` / `watched` 都是服务端算好下发的，这里只负责显示——前端不判断能看几集（CLAUDE.md 不变量 1），
 * 「看过」也不在本地记：换个设备进来就全忘了。
 * **只有当前这一部剧**——Mini App 一剧一页，不提供任何通往别的剧的入口。
 */
export function EpisodeSheet({ open, drama, currentId, playing, onSelect }: Props) {
  const episodes = drama.episodes
  const current = Math.max(0, episodes.findIndex((e) => e.id === currentId))
  const pageCount = Math.ceil(episodes.length / PAGE_SIZE)
  const [page, setPage] = useState<number | null>(null)
  const shownPage = page ?? Math.floor(current / PAGE_SIZE)
  const [expanded, setExpanded] = useState(false)
  const [truncated, setTruncated] = useState(false)
  const synopsisRef = useRef<HTMLParagraphElement>(null)

  // 每次打开都回到当前集那一段、简介收起
  useLayoutEffect(() => {
    if (!open) return
    setPage(null)
    setExpanded(false)
  }, [open])

  // 截断了才给「展开」：两行时的可见高度和完整高度比
  useLayoutEffect(() => {
    const el = synopsisRef.current
    if (open && el && !expanded) setTruncated(el.scrollHeight > el.clientHeight + 1)
  }, [open, expanded, drama.synopsisKm])

  const first = shownPage * PAGE_SIZE
  const shown = episodes.slice(first, first + PAGE_SIZE)

  return (
    <div
      style={{ ...sheet.wrap, ...(open ? sheet.wrapOpen : null) }}
      role="dialog"
      aria-modal={open}
      aria-hidden={!open}
    >
      <div style={sheet.handle} aria-hidden />
      <div style={sheet.scroll}>
        <div style={sheet.header}>
          <div style={sheet.cover}>
            {drama.coverUrl && <img src={drama.coverUrl} alt="" style={sheet.coverImg} />}
          </div>
          <div style={sheet.headText}>
            <h1 style={sheet.title}>{drama.titleKm}</h1>
            <span style={sheet.count}>
              {finished(drama)
                ? t.episodeTotal.replace('{n}', String(drama.totalEpisodes))
                : t.episodeUpdatedTo.replace('{n}', String(episodes.length))}
            </span>
          </div>
        </div>

        {drama.synopsisKm && (
          <div
            style={sheet.synopsisWrap}
            onClick={() => (truncated || expanded) && setExpanded(!expanded)}
          >
            <p ref={synopsisRef} style={{ ...sheet.synopsis, ...(expanded ? null : sheet.clamped) }}>
              {drama.synopsisKm}
            </p>
            {truncated && !expanded && <span style={sheet.expand}>{t.expand}</span>}
          </div>
        )}

        {drama.tags.length > 0 && (
          <div style={sheet.tags}>
            {drama.tags.slice(0, 4).map((tag) => (
              <span key={tag.id} style={sheet.tag}>
                {tag.nameKm}
              </span>
            ))}
          </div>
        )}

        {pageCount > 1 && (
          <div style={sheet.pages}>
            {Array.from({ length: pageCount }, (_, i) => (
              <button
                key={i}
                style={{ ...sheet.pageTab, ...(i === shownPage ? sheet.pageTabActive : null) }}
                onClick={() => setPage(i)}
              >
                {i * PAGE_SIZE + 1}-{Math.min((i + 1) * PAGE_SIZE, episodes.length)}
              </button>
            ))}
          </div>
        )}

        <div style={{ ...sheet.grid, marginTop: pageCount > 1 ? 0 : 16 }}>
          {shown.map((ep) => {
            const active = ep.id === currentId
            const seen = !!ep.watched && !active
            return (
              <button
                key={ep.id}
                onClick={() => onSelect(ep)}
                style={{
                  ...sheet.cell,
                  ...(seen ? sheet.cellWatched : null),
                  ...(ep.locked ? sheet.cellLocked : null),
                  ...(active ? sheet.cellActive : null),
                }}
                aria-current={active || undefined}
                aria-label={seen ? `${ep.number} ${t.watched}` : String(ep.number)}
              >
                {ep.number}
                {active ? (
                  <span className={playing ? 'playing-bars' : 'playing-bars paused'} style={sheet.corner} aria-hidden>
                    <i />
                    <i />
                    <i />
                  </span>
                ) : ep.locked ? (
                  <svg style={sheet.lock} width="9" height="10" viewBox="0 0 9 10" aria-hidden>
                    <path
                      d="M2 4V3a2.5 2.5 0 0 1 5 0v1h.5a1 1 0 0 1 1 1v4a1 1 0 0 1-1 1h-6a1 1 0 0 1-1-1V5a1 1 0 0 1 1-1H2Zm1.2 0h2.6V3a1.3 1.3 0 0 0-2.6 0v1Z"
                      fill="currentColor"
                    />
                  </svg>
                ) : null}
              </button>
            )
          })}
        </div>
      </div>
    </div>
  )
}

const sheet = {
  wrap: {
    position: 'fixed',
    left: 0,
    right: 0,
    bottom: 0,
    height: `${SHEET_RATIO * 100}dvh`,
    display: 'flex',
    flexDirection: 'column',
    background: 'var(--card)',
    color: 'var(--ink)',
    borderRadius: '16px 16px 0 0',
    transform: 'translateY(100%)',
    transition: 'transform .25s ease-in-out',
    pointerEvents: 'none',
    zIndex: 20,
  },
  wrapOpen: { transform: 'translateY(0)', pointerEvents: 'auto' },
  handle: {
    flex: '0 0 auto',
    width: 36,
    height: 4,
    borderRadius: 2,
    background: 'var(--ink-divider)',
    margin: '8px auto 0',
  },
  scroll: {
    flex: 1,
    overflowY: 'auto',
    padding: '24px 16px calc(24px + env(safe-area-inset-bottom))',
  },
  header: { display: 'flex', alignItems: 'flex-start', gap: 12 },
  cover: {
    flex: '0 0 auto',
    width: 36,
    height: 50,
    borderRadius: 6,
    overflow: 'hidden',
    background: '#ededed',
  },
  coverImg: { width: '100%', height: '100%', objectFit: 'cover' },
  headText: { minWidth: 0, display: 'flex', flexDirection: 'column', gap: 4 },
  title: {
    margin: 0,
    fontSize: 16,
    fontWeight: 700,
    lineHeight: 1.5,
    overflow: 'hidden',
    textOverflow: 'ellipsis',
    whiteSpace: 'nowrap',
  },
  count: { fontSize: 14, color: 'var(--ink-3)', lineHeight: 1.5 },
  synopsisWrap: { position: 'relative', marginTop: 12 },
  synopsis: { margin: 0, fontSize: 14, color: 'var(--ink-2)' },
  clamped: {
    display: '-webkit-box',
    WebkitLineClamp: 2,
    WebkitBoxOrient: 'vertical',
    overflow: 'hidden',
  },
  expand: {
    position: 'absolute',
    right: 0,
    bottom: 0,
    paddingLeft: 24,
    fontSize: 14,
    color: 'var(--ink-3)',
    background: 'linear-gradient(90deg, rgba(255,255,255,0), var(--card) 40%)',
  },
  tags: { display: 'flex', gap: 6, marginTop: 12, overflow: 'hidden' },
  tag: {
    flex: '0 0 auto',
    height: 24,
    padding: '0 8px',
    display: 'flex',
    alignItems: 'center',
    borderRadius: 4,
    fontSize: 12,
    color: 'var(--ink-2)',
    background: 'var(--ink-weak-bg)',
    whiteSpace: 'nowrap',
  },
  pages: { display: 'flex', marginTop: 4, marginLeft: -12, overflowX: 'auto' },
  pageTab: {
    flex: '0 0 auto',
    padding: '13px 12px',
    fontSize: 14,
    lineHeight: 1.3,
    color: 'var(--ink-3)',
    background: 'transparent',
  },
  pageTabActive: { color: 'var(--ink)', fontWeight: 700 },
  grid: { display: 'grid', gridTemplateColumns: 'repeat(6, 1fr)', gap: '8px 10px' },
  cell: {
    position: 'relative',
    aspectRatio: '1',
    display: 'grid',
    placeItems: 'center',
    borderRadius: 8,
    fontSize: 14,
    color: 'var(--ink)',
    background: 'var(--ink-weak-bg)',
  },
  cellWatched: { color: 'var(--ink-watched)' },
  cellLocked: { color: 'var(--ink-unavailable)' },
  cellActive: { color: 'var(--accent)', fontWeight: 700, background: 'var(--accent-weak)' },
  corner: { position: 'absolute', top: 4, right: 4 },
  lock: { position: 'absolute', top: 5, right: 5 },
} satisfies Record<string, React.CSSProperties>
